import { useEffect, useState } from 'react'
// redux
import { useSelector } from 'react-redux'
// hooks
import useUIActions from '../../../hooks/useUIActions'
import useRouterControl from '../../../hooks/useRouterControl'
// queries
import { useContentfulClasses } from '../../../hooks/contentful'
// services
import ClassesServices from '../services/classes'
import SubscriptionServices from '../../Subscription/services/subscription'
// libraries
import dayjs from '../../../utils/dayjs-config'
// constants
import getTimeHour from '../../../constants/getTimeHour'
// components
import ButtonBullet from '../../../components/atoms/button-bullet/button-bullet'
import Modal from '../../../components/atoms/modal/modal'
import Button from '../../../components/atoms/button/button'
import PopupTop from '../../../components/atoms/popup-top/popup-top'

export default function useClasses({
	location = false,
	month = false,
	day = false,
	id = false,
	isCycling = false,
	getData = true,
	getPopular = true
} = {}) {
	// redux
	const {
		session: { userWithoutLogin, email, userId: idUser },
		activePatient: { userId }
	} = useSelector(state => state)

	// hooks
	const { handleShowPopupTop, handleShowModal, handleCloseModal } = useUIActions()
	const { routerControl } = useRouterControl()

	// queries
	const {
		contClassesData: { modals }
	} = useContentfulClasses()

	// date
	const formatDate = 'dddd DD MMMM'

	// data
	const [loadingClasses, setLoadingClasses] = useState(false)
	const [dataClasses, setDataClasses] = useState(false)
	const [dataClassesById, setDataClassesById] = useState(false)
	const [dataPopularClasses, setDataPopularClasses] = useState(false)
	const [dataLessons, setDataLessons] = useState(false)
	const [numberOfReservation, setNumberOfReservation] = useState(0)
	const [quotas, setQuotas] = useState()
	const [isSubscriptionData, setIsSubscriptionData] = useState(false)
	const modalContent = {
		addLesson: {
			title: modals?.[0]?.tituloModal,
			description: ({ currentReservation, maxReservation = 4 }) =>
				`Te quedan ${
					maxReservation - currentReservation
				} clases disponibles para asistir este mes.`,
			img: modals?.[0]?.imagenCollection.items[0].url,
			button: (
				<Button
					title={modals?.[0]?.copyBoton}
					uppercase
					classes='pink mt1'
					onClick={() => handleCloseModal()}
				/>
			)
		},
		alreadyAdded: {
			title: modals?.[1]?.tituloModal,
			description: modals?.[1]?.descripcionModal,
			img: modals?.[1]?.imagenCollection?.items?.[0]?.url,
			button: (
				<Button
					title={modals?.[1]?.copyBoton}
					uppercase
					classes='mt1'
					onClick={() => handleCloseModal()}
				/>
			)
		},
		deleteLessons: {
			title: modals?.[2]?.tituloModal,
			description: modals?.[2]?.descripcionModal,
			img: modals?.[2]?.imagenCollection?.items?.[0]?.url
		},
		limitClasses: {
			title: modals?.[3]?.tituloModal,
			description: ({ date, maxReservation = 4 }) => {
				const splittedDate = date.split('/')
				const splittedDay = splittedDate[2].split(' ')
				const formattedDate = dayjs(
					`${splittedDate[0]}-${splittedDate[1]}-${splittedDay[0]}T${splittedDay[1]}`
				).format('DD MMMM')
				return `Espera hasta el próximo ${formattedDate} para volver a tener disponible tus ${maxReservation} clases.`
			},
			img: modals?.[3]?.imagenCollection?.items[0].url,
			button: (
				<Button
					title={modals?.[3]?.copyBoton}
					uppercase
					classes='mt1'
					onClick={() => handleCloseModal()}
				/>
			)
		}
	}

	// functions
	const normalizeText = text =>
		text
			.toLowerCase()
			.replace(/ /g, '_')
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')

	const normalizeProfessional = name => name?.split?.('@')[0] || false

	const frequencyHour = ({ init, end }) => {
		if (!init) return false
		return `${getTimeHour(init)} a ${getTimeHour(end)}`
	}

	const setDate = date => {
		if (!date) return

		const current = date.split('/')
		const baseDate =
			current && dayjs(`${current[0]}-${current[1]}-${current[2].split(' ')[0]}`).format(formatDate)
		return baseDate
	}

	const valid5Minutes = quotaDate => {
		const splitted = quotaDate.split('/')
		const currentDate = dayjs()
		const normalizeDate = dayjs(
			`${splitted[0]}-${splitted[1]}-${splitted[2].split(' ')[0]}T${splitted[2].split(' ')[1]}`
		)
		const validation = normalizeDate.diff(currentDate, 'minute')
		return validation < 5
	}

	const clearQuotas = () => setQuotas()

	// call data
	const getClasses = ({ location, month, day }) => {
		setLoadingClasses(true)
		ClassesServices.getClasses({
			userId: idUser || userId,
			email: userWithoutLogin ? email : null,
			location: normalizeText(location),
			month,
			day
		})
			.then(res => {
				if (res.status === 200) {
					const newData = res.data && res.data.sort(e => (!e.reserved ? 1 : !e.withSpace ? -1 : 1))
					setDataClasses(newData)
				}
			})
			.catch(error => {
				handleShowPopupTop(<PopupTop type='error' label={error.message} />)
			})
			.finally(() => setLoadingClasses(false))
	}

	const getClassesById = id => {
		setLoadingClasses(true)
		ClassesServices.getClassesById({
			userId: userId || null,
			email: userWithoutLogin ? email : null,
			id
		})
			.then(res => {
				if (res.status === 200) setDataClassesById(res.data)
				setLoadingClasses(false)
			})
			.catch(() => {
				setLoadingClasses(false)
			})
	}

	const getLesson = () => {
		setLoadingClasses(true)
		ClassesServices.getLessons({
			userId: idUser || null
		})
			.then(res => {
				res.status === 200 && setDataLessons(res.data)
				const newData = res.data.filter(e => e.id)
				setNumberOfReservation(newData.length)
			})
			.catch(error => {
				handleShowPopupTop(<PopupTop type='error' label={error.message} />)
			})
			.finally(() => setLoadingClasses(false))
	}

	const getCyclingLesson = id => {
		ClassesServices.getClassesCycling({
			userId: userId || null,
			email: userWithoutLogin ? email : null,
			id
		})
			.then(res => {
				if (res.status === 200) setDataClassesById(res.data)
			})
			.catch(error => {
				handleShowPopupTop(<PopupTop type='error' label={error.message} />)
			})
			.finally(() => setLoadingClasses(false))
	}

	const getPopularClasses = ({ location, month, day }) => {
		setLoadingClasses(true)
		ClassesServices.getPopularClasses({
			userId: userId || null,
			email: userWithoutLogin ? email : null,
			location: normalizeText(location),
			month,
			day
		})
			.then(res => {
				res.status === 200 && setDataPopularClasses(res.data)
			})
			.catch(error => {
				handleShowPopupTop(<PopupTop type='error' label={error.message} />)
			})
			.finally(() => setLoadingClasses(false))
	}

	const getSubscriptionData = () => {
		setLoadingClasses(true)
		SubscriptionServices.getSubscription({ userId: idUser })
			.then(res => {
				if (res.status === 200) {
					setIsSubscriptionData(res.data)
				}
			})
			.finally(() => setLoadingClasses(false))
	}

	const setLessons = ({ id, quotas, initialHour, finalHour, isCycling }) => {
		if (isSubscriptionData && numberOfReservation >= isSubscriptionData.maxLessonsInMonth) {
			return handleShowModal(
				<Modal
					title={modalContent.limitClasses.title}
					imagen={modalContent.limitClasses.img}
					description={modalContent.limitClasses.description({
						date: isSubscriptionData?.planTerminationDate,
						maxReservation: isSubscriptionData.maxLessonsInMonth
					})}
				>
					{modalContent.limitClasses.button}
				</Modal>
			)
		}

		setLoadingClasses(true)
		if (valid5Minutes(initialHour)) {
			setLoadingClasses(false)
			return handleShowPopupTop(<PopupTop type='error' label='No es posible hacer esta reserva.' />)
		}
		if (isCycling === true) {
			localStorage.setItem('cycling-init', initialHour)
			localStorage.setItem('cycling-final', finalHour)
			return routerControl({ link: `/centros-de-cuidado/reserva-bicicleta/${id}/${quotas}` })
		}

		const payload = {
			userId,
			email: userWithoutLogin ? email : null,
			id,
			quotas,
			initialHour,
			finalHour,
			bikesList: null
		}

		ClassesServices.setLessons({ userId: idUser, payload })
			.then(res => {
				if (res.status === 200) {
					location && getClasses({ location, month, day })
					id && getClassesById(id)
					getLesson()
					setQuotas()
					getSubscriptionData()
					return handleShowModal(
						<Modal
							title={modalContent.addLesson.title}
							imagen={modalContent.addLesson.img}
							description={modalContent.addLesson.description({
								currentReservation: numberOfReservation + 1,
								maxReservation: isSubscriptionData.maxLessonsInMonth
							})}
						>
							{modalContent.addLesson.button}
						</Modal>
					)
				}
				if (res.status === 400 && res.errorCode === 'EM027') {
					return handleShowModal(
						<Modal
							title={modalContent.alreadyAdded.title}
							imagen={modalContent.alreadyAdded.img}
							description={modalContent.alreadyAdded.description}
						>
							{modalContent.alreadyAdded.button}
						</Modal>
					)
				}
				return handleShowPopupTop(
					<PopupTop
						type='error'
						label='No es posible hacer esta reserva, por favor intenta más tarde.'
					/>
				)
			})

			.finally(() => {
				setLoadingClasses(false)
				getSubscriptionData()
			})
	}

	const setLessonsCycling = ({
		id,
		bikesList,
		quotas,
		initialHour,
		finalHour,
		callback = false
	}) => {
		if (valid5Minutes(initialHour)) {
			return handleShowPopupTop(<PopupTop type='error' label='No es posible hacer esta reserva.' />)
		}

		setLoadingClasses(true)

		const payload = {
			userId,
			email: userWithoutLogin ? email : null,
			id,
			bikesList,
			quotas,
			initialHour,
			finalHour
		}

		ClassesServices.setLessons({ userId: idUser, payload })
			.then(res => {
				setLoadingClasses(false)
				if (res.status === 200) {
					routerControl({ link: '/centros-de-cuidado/clases' })
					handleShowModal(
						<Modal
							title={modalContent.addLesson.title}
							imagen={modalContent.addLesson.img}
							description={modalContent.addLesson.description}
						>
							{modalContent.addLesson.button}
						</Modal>
					)
					return callback && callback()
				}
				if (res.status === 400 && res.errorCode === 'EM027') {
					return handleShowModal(
						<Modal
							title={modalContent.alreadyAdded.title}
							imagen={modalContent.alreadyAdded.img}
							description={modalContent.alreadyAdded.description}
						>
							{modalContent.alreadyAdded.button}
						</Modal>
					)
				}
				return handleShowPopupTop(
					<PopupTop
						type='error'
						label='No es posible hacer esta reserva, por favor intenta más tarde.'
					/>
				)
			})
			.catch(() => {
				setLoadingClasses(false)
			})
	}

	const setDeleteLessons = id => {
		setLoadingClasses(true)
		handleCloseModal()
		ClassesServices.cancelLessons({
			userId: userId || null,
			email: userWithoutLogin ? email : null,
			id
		})
			.then(res => {
				setLoadingClasses(false)
				if (res.status === 200) {
					location && getClasses({ location, month, day })
					id && getClassesById(id)
					getLesson()
					setQuotas()
					handleShowPopupTop(<PopupTop type='success' label='Reserva eliminada correctamente' />)
					return routerControl({ link: '/centros-de-cuidado/reserva-clases' })
				}
				return handleShowPopupTop(<PopupTop type='error' label={res.message} />)
			})
			.catch(error => {
				setLoadingClasses(false)
				// ojo este es temporal
				handleShowPopupTop(<PopupTop type='error' label={error.errorCode} />)
			})
	}

	const deleteLessons = id => {
		handleShowModal(
			<Modal
				title={modalContent.deleteLessons.title}
				imagen={modalContent.deleteLessons.img}
				description={modalContent.deleteLessons.description}
			>
				<Button
					title={modals[2].copyBoton}
					toUpperCase
					classes='mt1'
					onClick={() => setDeleteLessons(id)}
				/>
			</Modal>
		)
	}

	// renders
	const renderBullets = () => {
		let bullets = []
		const numberOfBullets = 2

		for (let i = 1; i <= numberOfBullets; i++) {
			bullets = [...bullets, i]
		}
		return bullets.map(b => (
			<ButtonBullet key={b} onClick={() => setQuotas(b)} active={quotas === b}>
				{b}
			</ButtonBullet>
		))
	}

	// button disabled
	const disableButton = ({ initialHour, dateFilter }) => {
		const format = 'YYYY/MM/DD HH:mm:ss'
		const currentDate = dayjs().format(format)
		const date = dayjs(initialHour, format).format(format)
		const limitDayQuotas = dataLessons?.filter(
			j => j.initialHour.split(' ')[0] === dateFilter
		).length
		if (currentDate > date) return true
		return numberOfReservation >= 3 || limitDayQuotas >= 2
	}

	useEffect(() => {
		if (!location || !month || !day) return
		getClasses({ location, month, day })

		!dataPopularClasses && getPopular && getPopularClasses({ location, month, day })
	}, [location, month, day, userId])

	useEffect(() => {
		if (!id || !getData) return

		if (!isCycling) return getClassesById(id)

		getCyclingLesson(id)
	}, [id])

	useEffect(() => {
		if (!location) return
		localStorage.setItem('activeHeadButton', normalizeText(location))
	}, [location])

	useEffect(() => {
		if (!getData) return null
		getLesson()
		getSubscriptionData()
	}, [])

	return {
		loadingClasses,
		dataClasses,
		dataClassesById,
		numberOfReservation,
		dataLessons,
		setLessons,
		setLessonsCycling,
		deleteLessons,
		dataPopularClasses,
		isSubscriptionData,

		// control data
		quotas,
		disableButton,

		// actions
		normalizeProfessional,
		frequencyHour,
		setDate,
		clearQuotas,

		// render
		renderBullets
	}
}
